import { Fragment, use, useEffect, useRef, useState } from "react";
import { findSubNode, getCustomisationValue, getGroupData } from "../../EditablePreview";
import VisualisedNode, { IMAGE_MODES } from "../VisualisedNode";
import './SlotsRandomiser.scss';
import { NODE_LIST_CONTROLLER } from "../../../../../../NodeList/NodeList";
import { COLOUR_NAME_TO_COLOUR_HEX, HEX_TO_RGBA } from "../../../../../../../helpers/Colours";
import gsap from 'gsap'

const getSubNodesThrottle = {
    lastCall: 0,
}

const getLeftArrowSvg = (width, arrowColour, arrowBorderWidth, arrowBorderColour) => {
    const height = width;
    const pointList = `0,0 ${width},${height / 2} 0,${height} 0,0`;
    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" xmlns="http://www.w3.org/2000/svg">
            <polygon points={pointList} fill={COLOUR_NAME_TO_COLOUR_HEX(arrowColour)} strokeWidth={arrowBorderWidth} stroke={COLOUR_NAME_TO_COLOUR_HEX(arrowBorderColour)} />
        </svg>
    )
}

const getRightArrowSvg = (width, arrowColour, arrowBorderWidth, arrowBorderColour) => {
    const height = width;
    const pointList = `${width},0 ${width},${height} 0,${height / 2} ${width},0`;
    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" xmlns="http://www.w3.org/2000/svg">
            <polygon points={pointList} fill={COLOUR_NAME_TO_COLOUR_HEX(arrowColour)} strokeWidth={arrowBorderWidth} stroke={COLOUR_NAME_TO_COLOUR_HEX(arrowBorderColour)} />
        </svg>
    )
}

const optionSpin = {
    optionsYOffset: 0,
    reelOptionIndexes: [0, 0, 0, 0, 0],
}

const optionsExpanded = {
    reel0: false,
    reel1: false,
    reel2: false,
    reel3: false,
    reel4: false,
    reelX0: 0,
    reelX1: 0,
    reelX2: 0,
    reelX3: 0,
    reelX4: 0,
    reelWidth: 0,
    expandedOptionsHeight: 100,

    closeAll: function() {
        this.reel0 = false;
        this.reel1 = false;
        this.reel2 = false;
        this.reel3 = false;
        this.reel4 = false;
    }
}

const SlotsRandomiser = (props) => {
    // console.log("SlotsRandomiser props:", props);

    const customisationVars = props.node.customisationVars;
    const previewScale = props.previewScale || 1;

    const randomizerDataNode = findSubNode(props?.node?.topNode, 'Randomizer Data');
    const randomizerOptionsNodes = [];
    for (let i = 0; i < 5; i++) {
        randomizerOptionsNodes.push(findSubNode(randomizerDataNode, `Reel ${i + 1}`));
    }
    // console.log("Randomizer options nodes:", randomizerOptionsNodes);
    const segmentBackgroundNode = findSubNode(props?.node?.topNode, 'Segment Backgrounds');
    // console.log("Segment background node:", segmentBackgroundNode);

    if (Date.now() - getSubNodesThrottle.lastCall > 250) {
        if (NODE_LIST_CONTROLLER.nodeListExists('customisation-workspace-left-list')) {
            NODE_LIST_CONTROLLER.getSubNodes('customisation-workspace-left-list', segmentBackgroundNode); // Ensure sub-nodes are loaded
            for (let i = 0; i < 5; i++) {
                NODE_LIST_CONTROLLER.getSubNodes('customisation-workspace-left-list', randomizerOptionsNodes[i]); // Ensure sub-nodes are loaded
            }
        } else {
            NODE_LIST_CONTROLLER.getSubNodes('customisation-workspace-left-list_' + props.node?.featureData?._id, segmentBackgroundNode); // Ensure sub-nodes are loaded
            for (let i = 0; i < 5; i++) {
                NODE_LIST_CONTROLLER.getSubNodes('customisation-workspace-left-list_' + props.node?.featureData?._id, randomizerOptionsNodes[i]); // Ensure sub-nodes are loaded
            }
        }
        // console.log("Throttled getSubNodes calls for randomizer options and segment backgrounds", props.node);
        getSubNodesThrottle.lastCall = Date.now();
    }

    const showHeaderImage = getCustomisationValue(customisationVars, 'hud.slot.general.showHeaderImage') ?? false;
    const headerPosition = getCustomisationValue(customisationVars, 'hud.slot.headerImage.position') ?? 'top-center';
    const headerOffsetX = getCustomisationValue(customisationVars, 'hud.slot.headerImage.xOffset') ?? 0;
    const headerOffsetY = getCustomisationValue(customisationVars, 'hud.slot.headerImage.yOffset') ?? 0;
    const headerScale = getCustomisationValue(customisationVars, 'hud.slot.headerImage.scale') ?? 100;

    // All the variables that affect the size of the carrier...
    const edgePadding = getCustomisationValue(customisationVars, 'hud.slot.carrierSettings.edgePadding') ?? 10;
    const carrierOutlineWidth = getCustomisationValue(customisationVars, 'hud.slot.carrierSettings.outlineThickness') ?? 2;
    const reelsOrientation = getCustomisationValue(customisationVars, 'setup.globalSettings.slotSettings.orientation') ?? 'horizontal';
    const reelsCount = getCustomisationValue(customisationVars, 'setup.globalSettings.slotSettings.reels') ?? 3;
    const showReelHeadings = getCustomisationValue(customisationVars, 'setup.globalSettings.slotSettings.showReelHeadings') ?? false;

    // These will update on an interval where we check the height of a div with id "reel_heading_1" and set the state vars if it changes
    const [reelHeadingHeight, setReelHeadingHeight] = useState(20);
    const reelHeadingHeightRef = useRef(null);
    useEffect(() => {
        reelHeadingHeightRef.current = reelHeadingHeight;
    }, [reelHeadingHeight]);
    useEffect(() => {
        const interval = setInterval(() => {
            const reelHeadingElement = document.getElementById('reelHeadingWrapper1');
            if (reelHeadingElement) {
                const height = reelHeadingElement.clientHeight;
                if (height !== reelHeadingHeightRef.current) {
                    setReelHeadingHeight(height);
                }
            }
        }, 200);
        return () => clearInterval(interval);
    }, []);

    const reelSpacing = getCustomisationValue(customisationVars, 'hud.slot.general.reelSpacing') ?? 10;
    const reelWidth = getCustomisationValue(customisationVars, 'hud.slot.general.reelWidth') ?? 50;
    const reelHeight = getCustomisationValue(customisationVars, 'hud.slot.general.reelHeight') ?? 100;
    const reelOutlineWidth = getCustomisationValue(customisationVars, 'hud.slot.reelWindowSettings.outlineThickness') ?? 2;
    const reelHeadingGap = showReelHeadings ? 10 : 0;

    const spinSpeed = getCustomisationValue(customisationVars, 'setup.globalSettings.slotSettings.spinSpeed') ?? 1;

    const autoSetSegmentHeight = getCustomisationValue(customisationVars, 'hud.slot.general.autoSetOptionHeight') ?? false;
    const segmentHeight = autoSetSegmentHeight ? reelWidth : (getCustomisationValue(customisationVars, 'hud.slot.general.optionHeight') ?? 20);
    const showLandingZoneHighlight = getCustomisationValue(customisationVars, 'hud.slot.general.showLandingZoneHighlight') ?? false;
    const showLandingZonePointers = getCustomisationValue(customisationVars, 'hud.slot.general.showLandingZonePointers') ?? false;
    const autoSetLandingZoneHighlightHeight = getCustomisationValue(customisationVars, 'hud.slot.landingHighlightSettings.matchSegmentHeight') ?? false;
    const landingHighlightHeight = autoSetLandingZoneHighlightHeight ? segmentHeight : (getCustomisationValue(customisationVars, 'hud.slot.landingHighlightSettings.height') ?? 10);

    const landingZoneHolderHeight = landingHighlightHeight + 10;
    const firstReelMiddleY = edgePadding + carrierOutlineWidth + (reelHeight / 2) + reelOutlineWidth + (reelHeadingGap * (showReelHeadings ? 1 : 0));

    // Slightly odd calculation makes the visual editor look identical to the actual disruptor. Weird that I have to add the carrier outline width on, but it is what it is.
    let calculatedCarrierHeight = edgePadding * 2 + reelOutlineWidth * 2 + (reelHeight * (reelsOrientation === 'vertical' ? reelsCount : 1)) + (reelSpacing * (reelsOrientation === 'vertical' ? reelsCount - 1 : 0)) + (reelHeadingGap * (showReelHeadings ? (reelsOrientation === 'vertical' ? reelsCount : 1) : 0));
    let calculatedCarrierWidth = edgePadding * 2 + (reelOutlineWidth * 2 * (reelsOrientation === 'horizontal' ? reelsCount : 1)) + (reelWidth * (reelsOrientation === 'horizontal' ? reelsCount : 1)) + (reelSpacing * (reelsOrientation === 'horizontal' ? reelsCount - 1 : 0));

    const reelOptionsNode = findSubNode(props.node, 'Options');
    const reelTypographyNode = findSubNode(reelOptionsNode, 'Typography');
    const segmentsNode = findSubNode(reelOptionsNode, `Segment Backgrounds`);
    // console.log("Reel options node for Slots Randomiser:", reelOptionsNode, "Reel typography node:", reelTypographyNode);

    const pointerSettings = getGroupData(customisationVars, 'hud.slot.selectorPointers', 0);
    const getLeftPointerStyle = (extraPad = 0) => { return {
        transform: `translate(-100%, -50%)`,
        position: 'absolute',
        left: `${(edgePadding + pointerSettings.edgeInset) * previewScale + extraPad}px`,
        top: firstReelMiddleY * previewScale + (showReelHeadings ? reelHeadingHeight : 0),
    }}
    const getRightPointerStyle = (extraPad = 0) => { return {
        transform: `translate(100%, -50%)`,
        position: 'absolute',
        right: `${(edgePadding + pointerSettings.edgeInset) * previewScale + extraPad}px`,
        top: firstReelMiddleY * previewScale + (showReelHeadings ? reelHeadingHeight : 0),
    }}

    const hideOptionsWhileSpinning = getCustomisationValue(customisationVars, 'setup.globalSettings.slotSettings.hideOptions') ?? false;
    const hiddenOptionBlur = getCustomisationValue(customisationVars, 'setup.globalSettings.slotSettings.hiddenOptionBlur') ?? 5;
    const hideOptionText = getCustomisationValue(customisationVars, 'setup.globalSettings.slotSettings.blurText') ?? false;
    const hideOptionImages = getCustomisationValue(customisationVars, 'setup.globalSettings.slotSettings.blurImages') ?? false;

    const tickPointers = getCustomisationValue(customisationVars, 'hud.slot.selectorPointers.tickPointers') ?? false;

    const OptionSegment = (props) => {
        // console.log("OptionSegment props:", props, `setup.options${props.reelIndex + 1}.options.name`);

        const segmentBackgroundsCount = props.segmentBackgroundsNode?.subNodes?.length ?? 0;
        const segmentBgIndex = props.optionIndex % segmentBackgroundsCount;

        let optionData = getGroupData(customisationVars, `setup.options${props.reelIndex + 1}.options`, props.optionIndex);
        if (Array.isArray(optionData)) {
            optionData = optionData[0];
        }
        let segmentBackgroundData = getGroupData(customisationVars, `hud.slot.segmentBgs`, segmentBgIndex);
        if (Array.isArray(segmentBackgroundData)) {
            segmentBackgroundData = segmentBackgroundData[0];
        }
        // console.log(`Reel ${props.reelIndex + 1} Option ${props.optionIndex + 1} data:`, optionData, "Segment background data:", segmentBackgroundData);

        const segmentBgSettings = optionData.overrideBg ? optionData : segmentBackgroundData;
        const segmentBgSettingsNode = optionData.overrideBg ? null : props.segmentBackgroundsNode?.subNodes?.[segmentBgIndex];
        const segmentBaseBgStyle = {};
        const segmentOverlayStyle = {opacity: segmentBgSettings.addOverlay ? segmentBgSettings.bgOpacity / 100 : 0};
        if (segmentBgSettings) {
            if (segmentBgSettings.addBaseColour) {
                // Add a background colour from segmentBgSettings.baseColour and apply opacity from segmentBgSettings.baseOpacity
                segmentBaseBgStyle.backgroundColor = HEX_TO_RGBA(segmentBgSettings.baseColour, segmentBgSettings.baseOpacity / 100);
            }
            if (segmentBgSettings.addOverlay) {
                switch (segmentBgSettings.bgType) {
                    case 'gradient':
                        segmentOverlayStyle.backgroundImage = `linear-gradient(${segmentBgSettings.bgGradientAngle}deg, ${segmentBgSettings.bgGradientFrom}, ${segmentBgSettings.bgGradientTo})`;
                        break;
                    case 'image':
                        segmentOverlayStyle.backgroundImage = `url(${segmentBgSettings.bgImage.srcUrl})`;
                        segmentOverlayStyle.backgroundSize = 'cover';
                        segmentOverlayStyle.backgroundPosition = 'center';
                        break;
                    default:
                        break;
                }
            }
        }

        let standardTextSettings = getGroupData(customisationVars, `hud.slot.segmentTextSettings`, 0);
        if (Array.isArray(standardTextSettings)) {
            standardTextSettings = standardTextSettings[0];
        }
        const styleSettings = optionData.overrideTextStyle ? optionData : standardTextSettings;
        const typographyNode = optionData.overrideTextStyle ? null : reelTypographyNode;
        const contentStyle = {gap: 10 * previewScale, padding: `0 ${getCustomisationValue(customisationVars, `hud.slot.reelWindowSettings.edgePadding`, 0) * previewScale}px`};
        contentStyle.color = styleSettings.fontColour;
        contentStyle.fontSize = styleSettings.fontSize * previewScale;
        contentStyle.fontWeight = styleSettings.isBold ? 'bold' : 'normal';
        contentStyle.fontStyle = styleSettings.isItalic ? 'italic' : 'normal';
        contentStyle.fontFamily = styleSettings.font;
        if (styleSettings.addShadow) {
            contentStyle.textShadow = `2px 2px ${styleSettings.shadowBlur}px ${HEX_TO_RGBA(styleSettings.shadowColour, styleSettings.shadowOpacity / 100)}`;
        }

        let standardContentSettings = getGroupData(customisationVars, `hud.slot.general`, 0);
        if (Array.isArray(standardContentSettings)) {
            standardContentSettings = standardContentSettings[0];
        }
        const imageUsedAsBg = standardContentSettings.useImageAsSegmentBackground;
        const contentTextVisible = optionData.overrideContent ? optionData.showOptionNamesText : standardContentSettings.showOptionNamesText;
        const contentImageVisible = optionData.overrideContent ? optionData.showOptionImages : standardContentSettings.showOptionImages;
        const imageBelowText = optionData.overrideContent ? optionData.showImageBelowText : standardContentSettings.showImageBelowText;

        if (contentImageVisible && imageUsedAsBg) {
            contentStyle.backgroundImage = `url(${optionData.image.srcUrl})`;
            contentStyle.backgroundSize = `${optionData.imageScale}%`;
            contentStyle.backgroundPosition = 'center';
            contentStyle.backgroundRepeat = 'no-repeat';
        }

        const textBlurStyle = {};
        const imageBlurStyle = {};
        if (hideOptionsWhileSpinning) {
            // If the wheel hasn't stopped yet or the options isn't chosen, add a heavy blur to optionStyle
            if (!props.chosen) {
                if (hideOptionText) {
                    textBlurStyle.filter = `blur(${hiddenOptionBlur * previewScale}px)`;
                }
                if (hideOptionImages) {
                    imageBlurStyle.filter = `blur(${hiddenOptionBlur * previewScale}px)`;
                }
            }
        }

        if (props.makeSelectable) {
            const optionDataNode = props.reelOptionsNode?.subNodes?.[props.optionIndex];
            // console.log("Making option selectable with node:", props, props.segmentBackgroundsNode?.subNodes?.[segmentBgIndex], optionDataNode);
            return (
                <VisualisedNode {...props} node={segmentBgSettingsNode ?? optionDataNode} dontVisualiseModuleSettings={{ position: true, dimensions: true, background: true }} forceSelectable={true}>
                    <div className="option-segment" style={{width: (reelWidth) * previewScale, height: (segmentHeight) * previewScale}}>
                        <div className="option-segment-base-bg" style={segmentBaseBgStyle} />
                        <div className="option-segment-overlay-bg" style={segmentOverlayStyle} />
                        <div className="option-segment-content" style={contentStyle}>
                            <VisualisedNode
                                {...props}
                                node={optionDataNode}
                                dontVisualiseModuleSettings={{ position: true, dimensions: true, background: true, fill: true }}
                                style={{position: 'absolute', top: '10%', left: '10%', right: '10%', bottom: '10%', zIndex: 2}}
                                forceSelectable={true}
                            ><></></VisualisedNode>
                            {contentImageVisible && !imageUsedAsBg &&
                                <img src={optionData.image.srcUrl} alt="" style={{...imageBlurStyle, order: imageBelowText ? 1 : 0, width: `${optionData.imageScale}%`}} />
                            }
                            {contentTextVisible &&
                                <VisualisedNode {...props} node={typographyNode ?? optionDataNode} style={{width: '100%', textAlign: 'center', alignContent: 'center', zIndex: 3}} dontVisualiseModuleSettings={{ position: true, dimensions: true, background: true, typography: true}} forceSelectable={true}>
                                    <div style={{...textBlurStyle, margin: 'auto'}}>{optionData.name}</div>
                                </VisualisedNode>
                            }
                        </div>
                    </div>
                </VisualisedNode>
            )
        }

        return (
            <div className="option-segment" style={{width: (reelWidth) * previewScale, height: (segmentHeight) * previewScale}}>
                <div className="option-segment-base-bg" style={segmentBaseBgStyle} />
                <div className="option-segment-overlay-bg" style={segmentOverlayStyle} />
                <div className="option-segment-content" style={contentStyle}>
                    {contentImageVisible && !imageUsedAsBg &&
                        <img src={optionData.image.srcUrl} alt="" style={{...imageBlurStyle, order: imageBelowText ? 1 : 0, width: `${optionData.imageScale}%`}} />
                    }
                    {contentTextVisible &&
                        <span style={textBlurStyle}>{optionData.name}</span>
                    }
                </div>
            </div>
        )
    }

    const Reel = (props) => {
        // console.log("Reel props:", props);
        const [refreshReelInc, setRefreshReelInc] = useState(0);

        const headingTextNode = findSubNode(props.reelVisualNode, `Headings`);
        const headingTextContentLink = `setup.globalSettings.slotSettings.heading_reel${props.reelIndex + 1}`;
        const headingTextContent = showReelHeadings ? getCustomisationValue(customisationVars, `setup.globalSettings.slotSettings.heading_reel${props.reelIndex + 1}`) : '';
        const headingItalic = showReelHeadings ? getCustomisationValue(customisationVars, `hud.slot.headingTextSettings.isItalic`) : false;

        // console.log(`Reel ${props.reelIndex} - headingTextNode:`, headingTextNode, "headingTextContentLink:", headingTextContentLink, "headingTextContent:", headingTextContent, "headingItalic:", headingItalic);

        const randoDataNode = findSubNode(props.node?.topNode ?? props.node, 'Randomizer Data');
        const reelOptionsNode = findSubNode(randoDataNode, `Reel ${props.reelIndex + 1}`);
        const optionsFromVars = getGroupData(customisationVars, `setup.options${props.reelIndex + 1}.options`, -1);
        const optionsCountFromVars = Array.isArray(optionsFromVars) ? optionsFromVars.length : 0;
        const optionsCount = (reelOptionsNode?.subNodes?.length > 0) ? reelOptionsNode.subNodes.length - 1 : optionsCountFromVars;
        const optionsOrder = [];
        while (optionsCount > 0 && optionsOrder.length * segmentHeight < reelHeight) {
            for (let i = 0; i < optionsCount; i++) {
                optionsOrder.push(i);
            }
        }
        for (let i = 0; i < Math.ceil(reelHeight / segmentHeight); i++) {
            optionsOrder.push(i % optionsCount);
        }

        const [optionsStartIndex, setOptionsStartIndex] = useState(optionSpin.reelOptionIndexes[props.reelIndex]);
        const optionsYOffset = useRef(optionSpin.optionsYOffset);

        let optionsVisibleCount = Math.ceil(reelHeight / segmentHeight) + 1;
        // We need to make sure our optionsVisibleCount is always an odd number, add 1 to it if it is even.
        if (optionsVisibleCount % 2 === 0) {
            optionsVisibleCount += 1;
        }

        const [previewWindowHeight, setPreviewWindowHeight] = useState(null);
        useEffect(() => {
            setTimeout(() => {
                const previewWindow = document.getElementById('previewWindow');
                const reelWindow = document.getElementById(`reel_${props.reelIndex}`);
                const randomiserContentElement = document.getElementById('randomiserContent');
                if (previewWindow) {
                    const previewRect = previewWindow.getBoundingClientRect();
                    setPreviewWindowHeight(previewRect.height);
                    optionsExpanded.expandedOptionsHeight = previewRect.height - 40;
                    optionsExpanded['reelX' + props.reelIndex] = reelWindow ? reelWindow.getBoundingClientRect().x - randomiserContentElement.getBoundingClientRect().x : 0;
                    optionsExpanded.reelWidth = reelWindow ? reelWindow.getBoundingClientRect().width + 40 : 100;
                }
            }, 10);
        }, [previewScale, reelWidth, reelSpacing, edgePadding, reelOutlineWidth]);

        useEffect(() => {
            const interval = setInterval(() => {
                const reelWidth_loc = getCustomisationValue(customisationVars, 'hud.slot.general.reelWidth') ?? 50;
                const reelHeight_loc = getCustomisationValue(customisationVars, 'hud.slot.general.reelHeight') ?? 100;
                const autoSetSegmentHeight_loc = getCustomisationValue(customisationVars, 'hud.slot.general.autoSetOptionHeight') ?? false;
                const segmentHeight_loc = autoSetSegmentHeight_loc ? reelWidth_loc : (getCustomisationValue(customisationVars, 'hud.slot.general.optionHeight') ?? 20);
                const isSpinning = props.timelinePlayheadLabel === 'Spinning' || props.timelinePlayheadLabel === 'Stopping Soon';   

                let spinSpeedMultiplier = 1;
                if (isSpinning) {
                    spinSpeedMultiplier = 2 + spinSpeed;
                }
                if (props.timelinePlayheadLabel !== 'Spin End' && props.timelinePlayheadLabel !== 'End') {
                    let prevYOffset = optionsYOffset.current;
                    let nextYOffset = optionsYOffset.current + (segmentHeight_loc * 0.01 * spinSpeedMultiplier);
                    if (nextYOffset >= segmentHeight_loc) {
                        nextYOffset = nextYOffset % segmentHeight_loc;
                        setOptionsStartIndex(prev => (prev + 1) % optionsCount);
                        optionSpin.reelOptionIndexes[props.reelIndex] = (optionSpin.reelOptionIndexes[props.reelIndex] + 1) % optionsCount;
                    }
                    optionsYOffset.current = nextYOffset;
                    optionSpin.optionsYOffset = nextYOffset;
                    const optionsListElement = document.getElementById(`optionsList_${props.reelIndex}`);
                    if (optionsListElement) {
                        optionsListElement.style.marginTop = `-${(segmentHeight_loc - nextYOffset) * previewScale}px`;
                    }

                    // Figure out if the bottom of a segment has passed the center of the reel, if it has we need to call props.setTickPointersFunc(true) to trigger the tick pointer animation
                    // This is not working properly and I have no idea why!!!
                    if (tickPointers) {
                        const marginTopNext = -(segmentHeight_loc - nextYOffset);
                        const marginTopPrev = -(segmentHeight_loc - prevYOffset);
                        let seg = 0;
                        while (seg < optionsVisibleCount) {
                            if (marginTopPrev + (seg * segmentHeight_loc) < (reelHeight_loc * 0.5) && marginTopNext + (seg * segmentHeight_loc) > (reelHeight_loc * 0.5)) {
                                const leftPointerHolder = document.getElementById('leftPointerHolder');
                                const rightPointerHolder = document.getElementById('rightPointerHolder');
                                if (leftPointerHolder && rightPointerHolder) {
                                    // Does the leftPointerHolder already have the tick class applied?
                                    /* if (!leftPointerHolder.classList.contains('left-pointer-tick' + (isSpinning ? '-fast' : ''))) {
                                        leftPointerHolder.classList.add('left-pointer-tick' + (isSpinning ? '-fast' : ''));
                                        rightPointerHolder.classList.add('right-pointer-tick' + (isSpinning ? '-fast' : ''));
                                        setTimeout(() => {
                                            leftPointerHolder?.classList?.remove?.('left-pointer-tick' + (isSpinning ? '-fast' : ''));
                                            rightPointerHolder?.classList?.remove?.('right-pointer-tick' + (isSpinning ? '-fast' : ''));
                                        }, isSpinning ? 50 : 150);
                                    } */
                                    // tick pointers
                                    gsap.killTweensOf(leftPointerHolder);
                                    gsap.fromTo(leftPointerHolder, {rotation: 0}, {rotation: 30, duration: 0.1, ease: 'none', yoyo: true, repeat: 1});
                                    gsap.killTweensOf(rightPointerHolder);
                                    gsap.fromTo(rightPointerHolder, {rotation: 0}, {rotation: -30, duration: 0.1, ease: 'none', yoyo: true, repeat: 1});
                                }
                                break;
                            }
                            seg++;
                        }
                    }
                } else {
                    // center the segments on the reel
                    const optionsHeight = optionsVisibleCount * segmentHeight_loc;
                    const offsetToCenter = (optionsHeight - reelHeight_loc) / 2;
                    const optionsListElement = document.getElementById(`optionsList_${props.reelIndex}`);
                    if (optionsListElement) {
                        optionsListElement.style.marginTop = `-${offsetToCenter * previewScale}px`;
                    }
                }
            }, 30);
            return () => clearInterval(interval);
        }, [segmentHeight, optionsCount, props.timelinePlayheadLabel]);

        const windowSettingsNode = findSubNode(props.reelVisualNode, `Window Settings`);
        if (windowSettingsNode) {
            windowSettingsNode.forceMirrorXResize = true;
            windowSettingsNode.forceMirrorYResize = true;
        }
        const highlightNode = findSubNode(findSubNode(props.node, 'Landing Zone'), 'Highlight');
        if (highlightNode) highlightNode.forceMirrorYResize = true;

        return (
            <>
                <div id={`reel_${props.reelIndex}`} className="reel" style={{gap: reelHeadingGap * previewScale}}>
                    {showReelHeadings && 
                        <VisualisedNode {...props} node={findSubNode(props.reelVisualNode, `Headings`)}>
                            <div id={`reelHeadingWrapper${props.reelIndex + 1}`} style={{textAlign: 'center', fontWeight: 'bold', margin: 'auto', fontStyle: headingItalic ? 'italic' : 'normal', whiteSpace: 'nowrap'}}>
                                <VisualisedNode {...props} textEditMode={true} textEditModeFieldPath={headingTextContentLink} textEditModeContentNode={headingTextNode} style={{width: 'fit-content'}} />
                            </div>
                        </VisualisedNode>
                    }
                    <VisualisedNode {...props} node={windowSettingsNode} style={{width: reelWidth * previewScale, height: reelHeight * previewScale, boxSizing: 'content-box'}}>
                        <div className="reel-content">
                            {showLandingZoneHighlight &&
                                <VisualisedNode {...props} node={highlightNode} style={{height: landingHighlightHeight * previewScale, position: 'absolute', width: '100%', left: 0, top: `${((reelHeight - landingHighlightHeight) / 2) * previewScale}px`}} dontVisualiseModuleSettings={{ dimensions: true}} />
                            }
                            <div id={`optionsList_${props.reelIndex}`} className={`options-list`} style={{filter: (props.timelinePlayheadLabel === 'Spinning' || props.timelinePlayheadLabel === 'Stopping Soon') ? `blur(${5 * previewScale}px)` : 'none', marginTop: `-${(segmentHeight - optionsYOffset.current) * previewScale}px`}}>
                                {Array.from({ length: optionsVisibleCount }).map((_, index) => {
                                    let optionIndex = (Math.floor(optionsStartIndex) + index) % optionsCount;
                                    return (
                                        <OptionSegment key={index} {...props} reelIndex={props.reelIndex} optionIndex={optionIndex} reelOptionsNode={reelOptionsNode} segmentBackgroundsNode={segmentsNode} chosen={(props.timelinePlayheadLabel === 'Spin End' || props.timelinePlayheadLabel === 'End') && index === Math.floor(optionsVisibleCount / 2)} />
                                    );
                                })}
                            </div>
                            {!props.thumbnailMode &&
                                <div className="expand-options-button" onClick={(e) => {optionsExpanded.closeAll(); optionsExpanded['reel' + props.reelIndex] = true; props.forceUpdateComponent(); setTimeout(() => { props.activateNodeFunction(findSubNode(props.reelVisualNode, 'Options')); props.setFocusEditingOnNode(findSubNode(props.reelVisualNode, 'Options')); }, 10); e.stopPropagation();}}>
                                    Expand Options
                                </div>
                            }
                        </div>
                    </VisualisedNode>
                </div>
            </>
        )
    }

    const headerNode = findSubNode(props.node, 'Header');
    if (headerNode) headerNode.applyScale = headerScale / 100;

    useEffect(() => {
        const optionsSubNode = findSubNode(props.node, 'Options');
        if (props.activeNode?.name === 'Reels' || (props.activeNode && props.activeNode.id !== optionsSubNode?.id && !findSubNode(optionsSubNode, props.activeNode.name) && !findSubNode(randomizerDataNode, props.activeNode.name))) {
            optionsExpanded.closeAll();
        }
    }, [props.activeNode]);

    return (
        <div className="slots-randomiser-content-holder" id="randomiserContent" style={{ width: (calculatedCarrierWidth + carrierOutlineWidth * 2) * previewScale, height: ((calculatedCarrierHeight + carrierOutlineWidth * 2) * previewScale) + (showReelHeadings ? reelHeadingHeight * (reelsOrientation === 'vertical' ? reelsCount : 1) : 0) }}>
            {showHeaderImage &&
                <div className={headerPosition}>
                    <div className="header-content">
                        <VisualisedNode {...props} node={headerNode} dontVisualiseModuleSettings={{ position: true, dimensions: true }} style={{transform: `translate(${headerOffsetX * headerScale / 100 * previewScale}px, ${headerOffsetY * headerScale / 100 * previewScale}px)`}} applyScale={headerScale / 100} imageMode={IMAGE_MODES.scaled} />
                    </div>
                </div>
            }
            <div className="main-content">
                <VisualisedNode {...props} node={props.node} onlyStyleVisualisation={true}>
                    <div style={{padding: edgePadding * previewScale}}>
                        <VisualisedNode {...props} node={findSubNode(props.node, 'Reels')} dontVisualiseModuleSettings={{ dimensions: true}} style={{padding: 0, margin: 0}}>
                            <div className="reels-holder" style={{ flexDirection: reelsOrientation === 'vertical' ? 'column' : 'row', gap: reelSpacing * previewScale }}>
                                {Array.from({ length: reelsCount }).map((_, index) => (
                                    <Reel key={index} {...props} reelIndex={index} reelVisualNode={findSubNode(props.node, `Reels`)} />
                                ))}
                            </div>
                        </VisualisedNode>
                    </div>
                    {(showLandingZoneHighlight || showLandingZonePointers) &&
                        <VisualisedNode {...props} node={findSubNode(props.node, 'Landing Zone')} style={{ width: calculatedCarrierWidth * previewScale, height: landingZoneHolderHeight * previewScale, position: 'absolute', left: carrierOutlineWidth * previewScale, top: (firstReelMiddleY - (landingZoneHolderHeight / 2)) * previewScale + (showReelHeadings ? reelHeadingHeight : 0) }} dontVisualiseModuleSettings={{ dimensions: true, border: true, fill: true }}>
                            {showLandingZoneHighlight &&
                                <VisualisedNode {...props} node={findSubNode(findSubNode(props.node, 'Landing Zone'), 'Highlight')} style={{ height: landingHighlightHeight * previewScale, width: '100%', marginTop: ((landingZoneHolderHeight - landingHighlightHeight) / 2) * previewScale }} dontVisualiseModuleSettings={{ dimensions: true, border: true, fill: true }} />
                            }
                        </VisualisedNode>
                    }
                </VisualisedNode>
            </div>
            {showLandingZonePointers &&
                <>
                    {pointerSettings.useImages &&
                        <>
                            <VisualisedNode {...props} node={findSubNode(props.node, 'Pointers')} style={{...getLeftPointerStyle(5), padding: 5}}>
                                <VisualisedNode {...props} node={findSubNode(props.node, 'Image Pointers')} dontVisualiseModuleSettings={{ fill: true, border: true }}>
                                    <div className={`ss-pointer-holder`} id="leftPointerHolder">
                                        <VisualisedNode {...props} node={findSubNode(findSubNode(props.node, 'Image Pointers'), 'Left Pointer')} applyScale={pointerSettings.scale / 100} imageMode={IMAGE_MODES.scaled} />
                                    </div>
                                </VisualisedNode>
                            </VisualisedNode>
                            <VisualisedNode {...props} node={findSubNode(props.node, 'Pointers')} style={{...getRightPointerStyle(5), padding: 5}}>
                                <VisualisedNode {...props} node={findSubNode(props.node, 'Image Pointers')} dontVisualiseModuleSettings={{ fill: true, border: true }}>
                                    <div className={`ss-pointer-holder`} id="rightPointerHolder">
                                        <VisualisedNode {...props} node={findSubNode(findSubNode(props.node, 'Image Pointers'), 'Right Pointer')} applyScale={pointerSettings.scale / 100} imageMode={IMAGE_MODES.scaled} />
                                    </div>
                                </VisualisedNode>
                            </VisualisedNode>
                        </>
                    }
                    {!pointerSettings.useImages &&
                        <>
                            <VisualisedNode {...props} node={findSubNode(props.node, 'Pointers')} style={{...getLeftPointerStyle(5), padding: 5}}>
                                <VisualisedNode {...props} node={findSubNode(props.node, 'Colored Pointers')} dontVisualiseModuleSettings={{ fill: true, border: true }}>
                                    <div className="ss-pointer-holder" id="leftPointerHolder">
                                        {getLeftArrowSvg(60 * previewScale * pointerSettings.scale / 100, pointerSettings.colour, pointerSettings.addBorder ? Math.ceil(pointerSettings.borderThickness * previewScale * pointerSettings.scale / 100) : 0, pointerSettings.borderColour)}
                                    </div>
                                </VisualisedNode>
                            </VisualisedNode>
                            <VisualisedNode {...props} node={findSubNode(props.node, 'Pointers')} style={{...getRightPointerStyle(5), padding: 5}}>
                                <VisualisedNode {...props} node={findSubNode(props.node, 'Colored Pointers')} dontVisualiseModuleSettings={{ fill: true, border: true }}>
                                    <div className="ss-pointer-holder" id="rightPointerHolder">
                                        {getRightArrowSvg(60 * previewScale * pointerSettings.scale / 100, pointerSettings.colour, pointerSettings.addBorder ? Math.ceil(pointerSettings.borderThickness * previewScale * pointerSettings.scale / 100) : 0, pointerSettings.borderColour)}
                                    </div>
                                </VisualisedNode>
                            </VisualisedNode>
                        </>
                    }
                </>
            }
            {Array.from({ length: reelsCount }).map((_, index) => {
                const optionsCount = findSubNode(findSubNode(props.node?.topNode ?? props.node, 'Randomizer Data'), `Reel ${index + 1}`)?.subNodes?.length - 1 ?? 0;
                return(
                    <Fragment key={index}>
                        {optionsExpanded['reel' + index] &&
                            <VisualisedNode {...props} node={findSubNode(props.node, 'Options')} style={{position: 'absolute', left: optionsExpanded['reelX' + index] - 20, top: '50%', transform: 'translateY(-50%)', width: optionsExpanded.reelWidth, height: optionsCount * segmentHeight * previewScale + 45 + (optionsCount - 1) * 5 + 10, maxHeight: optionsExpanded.expandedOptionsHeight - 10}} dontVisualiseModuleSettings={{ position: true, dimensions: true }}>
                                <div className="popup-panel v2-visual-sim-note expanded-reel-options-holder" style={{pointerEvents: 'auto', width: 'fit-content', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', height: 'fit-content'}}>
                                    <div style={{maxHeight: optionsExpanded.expandedOptionsHeight - 45, height: 'fit-content', overflowY: 'auto', width: 'fit-content', margin: '0 auto', paddingLeft: 10, paddingRight: 10, backgroundColor: '#ffffff'}}>
                                        <div className="options-list" style={{position: 'static', margin: `0px -${reelOutlineWidth}px`, padding: '10px 0', gap: 5, filter: 'none'}}>
                                            {Array.from({ length: optionsCount }).map((_, optionIndex) => {
                                                return (
                                                    <OptionSegment key={optionIndex} {...props} makeSelectable={true} reelIndex={index} optionIndex={optionIndex} reelOptionsNode={findSubNode(findSubNode(props.node?.topNode ?? props.node, 'Randomizer Data'), `Reel ${index + 1}`)} segmentBackgroundsNode={segmentsNode} chosen={true} />
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="v2-visual-sim-note-close-btn" style={{top: '0.1rem', right: '0.2rem'}} onClick={() => { optionsExpanded['reel' + index] = false; props.forceUpdateComponent(); }}>X</div>
                                </div>
                            </VisualisedNode>
                        }
                    </Fragment>
                )
            })}
        </div>
    )
}

export default SlotsRandomiser;
